import React, { useCallback, useMemo } from "react";

import Event from "@mui/icons-material/Event";
import { DateTime } from "luxon";

import { DATE_FORMAT } from "@waterly/utils-isomorphic";

import { BasicDatePicker, DatePickerActionBar, type DatePickerActionBarProps } from "../../pickers";

import type { MetricInputProps } from "./DataEntryInput.types";
import { InputMode } from "./DataEntryInput.types";
import { loadingInputSx } from "./loadingInputSx";
import { useInputSx } from "./useInputSx";

export type DateInputProps = MetricInputProps & {
  isLoading: boolean;
  timeZone: string;
};

export const DateInput: React.FC<DateInputProps> = ({
  id,
  onChange,
  value,
  isDirty,
  mode,
  isLoading,
  timeZone,
}) => {
  const inputDisabled = mode === InputMode.ReadOnly || mode === InputMode.SiteEdit || isLoading;
  const inputReadOnly = inputDisabled || mode === InputMode.Loading;
  const inputSx = useInputSx({ mode, isDirty });

  const dateTimeValue = useMemo(() => {
    if (!value) {
      return null;
    }
    return DateTime.fromFormat(value, DATE_FORMAT, { zone: timeZone });
  }, [timeZone, value]);

  const handleDateChange = useCallback(
    (date: DateTime | null) => {
      if (!date) {
        return onChange("");
      }
      return onChange(date.toFormat(DATE_FORMAT));
    },
    [onChange],
  );

  const handleSetNow = useCallback(() => {
    const now = DateTime.now().setZone(timeZone);
    handleDateChange(now);
  }, [handleDateChange, timeZone]);

  const actionBar = useCallback(
    (props: DatePickerActionBarProps) => {
      return <DatePickerActionBar {...props} onSetToday={handleSetNow} />;
    },
    [handleSetNow],
  );

  return (
    <BasicDatePicker
      value={dateTimeValue}
      onChange={handleDateChange}
      disabled={inputDisabled}
      sx={isLoading ? loadingInputSx : undefined}
      slotProps={{
        actionBar: {
          actions: ["cancel", "clear", "today"],
        },
        textField: {
          fullWidth: true,
          InputProps: {
            readOnly: inputReadOnly,
            sx: inputSx,
            placeholder: "",
            endAdornment: <Event />,
          },
          inputProps: {
            "data-id": id,
            "data-component": "MetricInput",
          },
        },
      }}
      slots={{
        actionBar: actionBar,
      }}
    />
  );
};
